<template>
  <v-autocomplete
    v-model="model"
    class="w-full"
    color="primary20"
    label="Buscar Política *"
    theme="light"
    :items="policies"
    item-title="name"
    item-value="id"
    return-object
    density="comfortable"
    :loading="isLoading"
    variant="outlined"
    hide-details
    no-data-text="Nenhuma política encontrada"
    @update:search="onSearch"
    @scroll.passive="onScroll"
  >
    <template #item="{ props, item }">
      <v-list-item v-bind="props">
        <v-list-item-title>{{ item.name }}</v-list-item-title>
        <v-list-item-subtitle>{{ item.company?.name }}</v-list-item-subtitle>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue'
import type { PropType } from 'vue'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { DsPolicySearchParams } from '@/Network/Types/Requests/DsPolicyRequests'
import type {
  PolicyOptionItem,
  PolicyOptionsFetcher
} from '@/components/designSystem/PolicyLinksManager/types'

const props = defineProps({
  modelValue: [String, Number, Object],
  companyId: Number,
  // Fonte alternativa de opções (ex: políticas de repasse). Sem ela, usa Network.policy.list
  fetchPolicies: {
    type: Function as PropType<PolicyOptionsFetcher>,
    required: false,
    default: undefined
  }
})
const emit = defineEmits(['update:modelValue'])
const { notifyError } = useErrorToast()

const model = ref(props.modelValue)
const policies = ref<PolicyOptionItem[]>([])
const isLoading = ref(false)
const searchTerm = ref('')
const offset = ref(0)
const limit = 30
const allLoaded = ref(false)

watch(
  () => props.modelValue,
  (val) => (model.value = val)
)
watch(
  () => model.value,
  (val) => emit('update:modelValue', val)
)

const onSearch = async (search: string) => {
  searchTerm.value = search
  offset.value = 0
  policies.value = []
  allLoaded.value = false
  await fetchDsPolicies()
}

const appendPage = (newItems: PolicyOptionItem[]) => {
  if (newItems.length < limit) allLoaded.value = true

  const existingIds = new Set(policies.value.map((p) => p.id))
  const uniqueItems = newItems.filter((item) => !existingIds.has(item.id))

  policies.value.push(...uniqueItems)
  offset.value += limit
}

const fetchDsPolicies = async () => {
  if (isLoading.value || allLoaded.value) return

  isLoading.value = true
  try {
    if (props.fetchPolicies) {
      const items = await props.fetchPolicies({
        q: searchTerm.value,
        companyId: props.companyId,
        offset: offset.value,
        limit
      })
      appendPage(items)
      return
    }

    const filters: DsPolicySearchParams = {
      q: searchTerm.value,
      isActive: true,
      limit: 0,
      offset: 0
    }

    if (props.companyId) {
      filters.companyIds = `${props.companyId}`
    }

    const response = await Network.policy.list(filters)

    appendPage(response?.data ?? [])
  } catch (error) {
    // Sem o aviso o select mostrava "Nenhuma política encontrada", como se não houvesse.
    notifyError(error, 'Não foi possível carregar as políticas.')
  } finally {
    isLoading.value = false
  }
}

const onScroll = (event: Event) => {
  const target = event.target as HTMLElement
  if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
    fetchDsPolicies()
  }
}

onMounted(() => {
  fetchDsPolicies()
})
</script>
